/* PolicyIntel UI v2 — Stock Workspace / Phase 4
 * Uses the existing public stock projection, saved-stock authority and
 * MarketSeriesChart. No new API or interest-theme dependency.
 * The critical path waits ONLY for the public stock projection. Other sources
 * resolve independently and never clear a previously completed projection.
 */
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, Skeleton, Stack, Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { addLiveSummaryHolding, getLiveSummaryHoldings, getLiveSummaryChanges, getLiveSummaryChangeEvents } from '../api/liveSummaryApi';
import { getProfessionalFinancialHistory } from '../api/professionalScreenerApi';
import { trackUxEvent } from '../analytics/analytics';

const MarketSeriesChart = React.lazy(() => import('../components/stock/MarketSeriesChart'));

type Raw = Record<string, any>;
type Tab = 'SUMMARY' | 'CHANGE' | 'EVIDENCE' | 'FINANCIAL' | 'CHART';
type Annual = Raw & { year: string; fsDiv: string };
const C = {bg:'#F6F8FB',white:'#FFFFFF',ink:'#122033',muted:'#607086',line:'#E2E8F0',navy:'#10263F',blue:'#2F6BFF',risk:'#D84C57',green:'#178A52',unknown:'#77869A'};
const txt = (v: unknown): string => v == null ? '' : String(v).trim();
const first = (...xs: unknown[]): string => xs.map(txt).find(Boolean) || '';
const tickerOf = (v: unknown): string => txt(v).match(/^A?(\d{6})$/)?.[1] || '';
const num = (v: unknown): number | null => v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
const pct = (v: number | null): string => v === null ? '미확인' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
const money = (v: number | null): string => v === null ? '가격 확인 중' : `${Math.round(v).toLocaleString('ko-KR')}원`;
function won(v: unknown): string {
  const n = num(v);
  if (n === null) return '—';
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(2)}조`;
  return `${(n / 1e8).toFixed(1)}억`;
}
function textList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((item) => typeof item === 'string' ? item.trim() :
    item && typeof item === 'object' ? first(item.summary, item.reason, item.description, item.title, item.label, item.message) : '')
    .filter(Boolean);
}
function textFromChange(row: Raw): string {
  return first(row.title, row.reason, row.message, row.summary, row.changeType, row.eventType);
}
function changeTicker(row: Raw): string {
  const direct = tickerOf(row.ticker ?? row.stockCode ?? row.stock_code);
  if (direct) return direct;
  return first(row.entityKey, row.entity_key).match(/(?:^|[^0-9])(\d{6})(?:$|[^0-9])/)?.[1] || '';
}
function normalizeAnnual(response: unknown, ticker: string): Annual[] {
  const root: Raw = response !== null && typeof response === 'object' ? response as Raw : {};
  const src: unknown[] = Array.isArray(root) ? root
    : Array.isArray(root.items) ? root.items
    : Array.isArray(root.data?.items) ? root.data.items
    : Array.isArray(root.data) ? root.data : [];
  const byBasis: Record<'CFS' | 'OFS', Map<string, Annual>> = {
    CFS: new Map<string, Annual>(), OFS: new Map<string, Annual>(),
  };
  for (const raw of src) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const row = raw as Raw;
    const code = tickerOf(row.ticker ?? row.stockCode ?? row.stock_code ?? ticker);
    const year = txt(row.year);
    const basis = first(row.fsDiv, row.fs_div).toUpperCase();
    if (code !== ticker || !/^\d{4}$/.test(year) || (basis !== 'CFS' && basis !== 'OFS')) continue;
    byBasis[basis].set(year, { ...row, year, fsDiv:basis });
  }
  // Prefer consolidated reporting. If only one consolidated year exists while
  // multiple standalone years exist, use the coherent standalone series instead.
  const chosen: 'CFS'|'OFS' = byBasis.CFS.size >= 2 || byBasis.OFS.size === 0 ? 'CFS' : 'OFS';
  return Array.from(byBasis[chosen].values())
    .sort((a,b) => Number(b.year) - Number(a.year)).slice(0,5);
}
function yearOverYear(rows: Annual[], field: string): number | null {
  if (rows.length < 2 || Number(rows[0].year) !== Number(rows[1].year) + 1 || rows[0].fsDiv !== rows[1].fsDiv) return null;
  const previous = num(rows[1][field]); const current = num(rows[0][field]);
  return previous !== null && previous > 0 && current !== null ? (current / previous - 1) * 100 : null;
}

// All financial commentary below is derived only from recorded annual values.
function financialDeltaLabel(value: number | null): string {
  if (value === null) return '비교 가능한 연속 연도 없음';
  if (value > 0) return '전년 대비 증가';
  if (value < 0) return '전년 대비 감소';
  return '전년 대비 변동 없음';
}
function financeTrendMessage(revenueYoY: number | null, operatingYoY: number | null): string {
  if (revenueYoY === null && operatingYoY === null) return '연속 회계연도의 비교 근거가 부족합니다. 아래 수치를 개별 확인해 주세요.';
  if (revenueYoY !== null && operatingYoY !== null) {
    if (revenueYoY >= 0 && operatingYoY < 0) return '매출은 늘었지만 영업이익은 감소했습니다. 수익성 변화의 원인을 확인해야 합니다.';
    if (revenueYoY < 0 && operatingYoY >= 0) return '매출은 감소했지만 영업이익은 증가했습니다. 비용 구조 변화를 함께 확인해야 합니다.';
    if (revenueYoY >= 0 && operatingYoY >= 0) return '매출과 영업이익이 모두 전년 대비 증가했습니다. 증가세의 지속성은 별도로 확인해야 합니다.';
    return '매출과 영업이익이 모두 전년 대비 감소했습니다. 실적 둔화 여부를 확인해야 합니다.';
  }
  return '일부 전년 비교 값만 확인됩니다. 확인 가능한 항목을 기준으로 해석합니다.';
}

function block(label: string, text: string, highlight = false) {
  return <Box sx={{p:{xs:2,md:2.7},bgcolor:highlight?'#F0F5FF':C.white,border:`1px solid ${highlight?'#D5E3FD':C.line}`,borderRadius:3,minWidth:0}}>
    <Typography sx={{fontSize:12,fontWeight:800,color:C.muted,mb:1}}>{label}</Typography>
    <Typography sx={{fontSize:14.5,lineHeight:1.8,fontWeight:highlight?750:550,overflowWrap:'anywhere'}}>{text}</Typography>
  </Box>;
}
class ChartBoundary extends React.Component<{children: React.ReactNode},{failed:boolean}> {
  state = {failed:false};
  static getDerivedStateFromError() { return {failed:true}; }
  render() {
    return this.state.failed
      ? <Alert severity="warning">차트 렌더링에 실패했습니다. 기존 전문 차트에서 확인해 주세요.</Alert>
      : this.props.children;
  }
}

export default function StockWorkspacePage() {
  const {ticker: param} = useParams<{ticker:string}>();
  const ticker = tickerOf(param);
  const navigate = useNavigate();
  const location = useLocation();
  const deepLinkTab = new URLSearchParams(location.search).get('tab')?.toUpperCase();
  const deepLinkEventId = new URLSearchParams(location.search).get('changeEventId') || '';
  const [focusedEvent,setFocusedEvent] = useState<Raw | null>(null);
  const [focusedError,setFocusedError] = useState('');
  const [tab,setTab] = useState<Tab>('SUMMARY');
  const [overview,setOverview] = useState<Raw|null>(null);
  const [overviewLoading,setOverviewLoading] = useState(true);
  const [overviewError,setOverviewError] = useState('');
  const [overviewRetry,setOverviewRetry] = useState(0);
  const [changes,setChanges] = useState<Raw[]>([]);
  const [changesLoading,setChangesLoading] = useState(false);
  const [changesError,setChangesError] = useState('');
  const [changeRetry,setChangeRetry] = useState(0);
  const [financial,setFinancial] = useState<Annual[]>([]);
  const [financialTicker,setFinancialTicker] = useState('');
  const [finLoading,setFinLoading] = useState(false);
  const [finError,setFinError] = useState('');
  const [finRetry,setFinRetry] = useState(0);
  const [finMetric,setFinMetric] = useState<'revenue'|'operatingIncome'|'netIncome'|'freeCashFlow'>('revenue');
  const [finExpanded,setFinExpanded] = useState(false);
  const [watchStatus,setWatchStatus] = useState<'loading'|'saved'|'not-saved'|'unknown'>('loading');
  const [saving,setSaving] = useState(false);
  const [saveFeedback,setSaveFeedback] = useState('');
  const saveLock = useRef(false);

  // On ticker change the old stock must NEVER appear under the new ticker header.
  useEffect(() => {
    setTab('SUMMARY');setOverview(null);setOverviewLoading(true);setOverviewError('');
    setChanges([]);setChangesLoading(false);setChangesError('');
    setFinancial([]);setFinancialTicker('');setFinLoading(false);setFinError('');setFinMetric('revenue');setFinExpanded(false);
    setWatchStatus('loading');setSaveFeedback('');
    setOverviewRetry(0);setChangeRetry(0);setFinRetry(0);
  },[ticker]);

  // Deep links select the requested tab after ticker initialization.
  useEffect(() => {
    if (['SUMMARY','CHANGE','EVIDENCE','FINANCIAL','CHART'].includes(deepLinkTab ?? '')) {
      setTab(deepLinkTab as Tab);
    }
  },[ticker,deepLinkTab]);

  // Only display an event if the server provides it for THIS ticker.
  useEffect(() => {
    let active = true;
    setFocusedEvent(null); setFocusedError('');
    if (!ticker || !deepLinkEventId) return () => { active=false; };
    const recent = changes.find(row =>
      String(row.eventId ?? row.id ?? '') === deepLinkEventId && changeTicker(row) === ticker);
    if (recent) {
      setFocusedEvent(recent);
      return () => { active=false; };
    }
    void getLiveSummaryChangeEvents({limit:100,timeoutMs:8000})
      .then(rows => {
        if (!active) return;
        const found = rows.find(row =>
          String(row.id ?? '') === deepLinkEventId && changeTicker(row as Raw) === ticker);
        if (found) {
          setFocusedEvent(found as Raw);
          trackUxEvent('change_view', {stock_code:ticker,change_event_id:deepLinkEventId,source:'alert-inbox'});
        } else {
          setFocusedError('이 이벤트의 상세 기록이 현재 조회 범위에 없습니다. 아래 최근 변화 및 근거를 확인해 주세요.');
        }
      })
      .catch(() => { if(active) setFocusedError('선택한 이벤트의 상세 기록을 조회하지 못했습니다.'); });
    return () => {active=false;};
  },[ticker,deepLinkEventId,changes]);

  useEffect(() => {
    if (!ticker) {setOverviewLoading(false);setOverviewError('유효한 6자리 종목코드가 아닙니다.');return;}
    let active = true;
    const controller = new AbortController();
    setOverviewLoading(true);setOverviewError('');
    const started = performance.now();
    trackUxEvent('stock_check_view',{stock_code:ticker,source:'stock-workspace-v2',stage:'open'});
    void fetch(`/api/main/market-intelligence/stocks/${encodeURIComponent(ticker)}`,{
      credentials:'include',headers:{Accept:'application/json'},signal:controller.signal,
    }).then(async res => {if(!res.ok) throw new Error(`HTTP_${res.status}`);return res.json();})
      .then((value: Raw) => {
        if(!active)return;
        const actual = tickerOf(value?.stock?.ticker ?? value?.stock?.stockCode ?? value?.stock?.stock_code);
        if(actual!==ticker)throw new Error('TICKER_MISMATCH');
        setOverview(value);
        trackUxEvent('stock_check_result',{stock_code:ticker,source:'stock-workspace-v2',result:'success',first_answer_ms:Math.round(performance.now()-started)});
      })
      .catch((err: unknown) => {
        if(!active)return;
        setOverviewError(err instanceof Error && err.message==='TICKER_MISMATCH'
          ? '다른 종목의 데이터가 반환돼 표시를 보류했습니다.'
          : '현재 종목의 판단 데이터를 불러오지 못했습니다. 다시 시도해 주세요.');
        trackUxEvent('stock_check_failed',{stock_code:ticker,source:'stock-workspace-v2'});
      }).finally(()=>{if(active)setOverviewLoading(false);});
    return()=>{active=false;controller.abort();};
  },[ticker,overviewRetry]);

  // Saved-stock authority is independently loaded; its failure must not block
  // the public stock answer or cause the UI to claim "not saved" incorrectly.
  useEffect(()=>{
    if(!ticker)return;
    let active=true;
    setWatchStatus('loading');
    void getLiveSummaryHoldings({refresh:false,timeoutMs:10000})
      .then(rows=>{if(active)setWatchStatus(rows.some(row=>tickerOf(row.ticker)===ticker)?'saved':'not-saved');})
      .catch(()=>{if(active)setWatchStatus('unknown');});
    return()=>{active=false;};
  },[ticker]);

  const fetchChanges = useCallback(() => setChangeRetry(v=>v+1),[]);
  useEffect(()=>{
    if(!ticker)return;
    let active=true;
    const controller = new AbortController();
    setChangesLoading(true);setChangesError('');
    void getLiveSummaryChanges({limit:100,timeoutMs:7000,signal:controller.signal})
      .then(result=>{
        if(!active)return;
        setChanges((Array.isArray(result?.changes)?result.changes:[]).filter(row=>changeTicker(row as Raw)===ticker) as Raw[]);
      })
      .catch(()=>{if(active){setChanges([]);setChangesError('변화 이력을 불러오지 못했습니다.');}})
      .finally(()=>{if(active)setChangesLoading(false);});
    return()=>{active=false;controller.abort();};
  },[ticker,changeRetry]);

  // Annual financials are needed only when the user opens FINANCIAL.
  // This avoids blocking the primary judgement with a slow secondary API.
  useEffect(()=>{
    if(tab!=='FINANCIAL'||!ticker)return;
    let active=true;
    setFinLoading(true);setFinError('');setFinancialTicker('');
    void getProfessionalFinancialHistory(ticker,5)
      .then(response=>{
        if(!active)return;
        const rows=normalizeAnnual(response,ticker);
        setFinancial(rows);setFinancialTicker(ticker);
        if(!rows.length)setFinError('연간 재무 조회는 완료됐지만 표시 가능한 회계연도 데이터가 없습니다.');
      })
      .catch(()=>{if(active){setFinancial([]);setFinancialTicker(ticker);setFinError('재무 API 호출에 실패했습니다. 다시 조회해 주세요.');}})
      .finally(()=>{if(active)setFinLoading(false);});
    return()=>{active=false;};
  },[ticker, tab, finRetry]);

  const save = async () => {
    if(!ticker||saveLock.current||watchStatus==='saved')return;
    saveLock.current=true;setSaving(true);setSaveFeedback('');
    trackUxEvent('stock_track_click',{ticker,stock_code:ticker,source:'stock-workspace-v2',intent:'WATCHLIST'});
    try {
      const result=await addLiveSummaryHolding(ticker);
      if(result.active===false||tickerOf(result.ticker)!==ticker)throw new Error('등록 결과의 종목코드가 일치하지 않습니다.');
      const verified=await getLiveSummaryHoldings({refresh:true,timeoutMs:12000});
      if(!verified.some(row=>tickerOf(row.ticker)===ticker))throw new Error('등록 요청 후 서버 목록 반영을 확인하지 못했습니다.');
      setWatchStatus('saved');setSaveFeedback('관심종목 등록이 확인됐습니다. 내 종목에서 변화와 위험을 감시할 수 있습니다.');
      const mutationEvent = result.action === 'CREATED'
        ? 'stock_track_created'
        : result.action === 'RESTORED'
          ? 'stock_track_restored'
          : 'stock_track_already_active';
      trackUxEvent(mutationEvent,{ticker,stock_code:ticker,source:'stock-workspace-v2',mutation_action:result.action});
      window.dispatchEvent(new Event('policy-intel:watchlist-updated'));
    } catch(err: unknown) {
      setSaveFeedback(err instanceof Error ? err.message : '감시종목 등록에 실패했습니다.');
    } finally {saveLock.current=false;setSaving(false);}
  };

  const visibleOverview = tickerOf(overview?.stock?.ticker ?? overview?.stock?.stockCode ?? overview?.stock?.stock_code) === ticker ? overview : null;
  const stock=(visibleOverview?.stock??{}) as Raw;
  const judgment=(visibleOverview?.valueJudgment??{}) as Raw;
  const snap=(visibleOverview?.confirmedSnapshot??{}) as Raw;
  const company=first(stock.corpName,stock.corp_name,stock.name,ticker);
  const price=num(stock.currentPrice??stock.current_price??snap.closePrice);
  const rate=num(stock.changeRate??stock.change_rate??snap.priceChangePct);
  const stateCode=first(judgment.valueState,judgment.state,judgment.valueStateLabel).toUpperCase();
  const stateLabels:Record<string,string>={VALUE_PRESERVED:'가치 보존',PRESERVED:'가치 보존',STABLE:'안정',REVIEW:'재검토',REVIEW_REQUIRED:'재검토 필요',DAMAGED:'훼손',UNKNOWN:'확인 필요',NO_DATA:'근거 부족'};
  const judgmentText=first(judgment.valueStateLabel,judgment.lastVerifiedValueStateLabel,stateLabels[stateCode],(/^[A-Z_]+$/.test(stateCode)?'':stateCode),'판단 확인 필요');
  const reasons=textList(judgment.reasons);
  const valueChanges=textList(judgment.recentValueChanges);
  const marketChanges=textList(judgment.recentMarketChanges);
  const nextChecks=textList(judgment.nextChecks);
  const reason=reasons[0]||'현재 판단을 뒷받침하는 검증된 근거를 확인 중입니다.';
  const next=nextChecks[0]||'다음 확인 조건이 아직 제공되지 않았습니다.';
  const visibleChanges = changes.filter(row=>changeTicker(row)===ticker);
  const changeSummary=[...valueChanges,...marketChanges,...visibleChanges.map(textFromChange)].find(Boolean)
    || (changesLoading?'변화 이력을 확인 중입니다.':'현재 확인된 변화가 없습니다. 관측 누락 가능성은 별도로 확인해야 합니다.');
  const basis=first(stock.sessionDate,stock.session_date,snap.tradeDate,snap.sessionDate,judgment.dataQuality?.fundamentalBasisDate,'기준일 확인 필요');
  const allChanges=Array.from(new Set([...valueChanges,...marketChanges,...visibleChanges.map(textFromChange)].filter(Boolean)));
  const toLegacy=()=>navigate(`/stocks?ticker=${ticker}&expand=${ticker}&q=${ticker}`);

const visibleFinancial = financialTicker===ticker ? financial : [];
const finLatest = visibleFinancial[0];
const revenueYoY = yearOverYear(visibleFinancial,'revenue');
const operatingYoY = yearOverYear(visibleFinancial,'operatingIncome');
const latestRevenue = finLatest ? num(finLatest.revenue) : null;
const latestOperating = finLatest ? num(finLatest.operatingIncome) : null;
const latestFcf = finLatest ? num(finLatest.freeCashFlow) : null;
const margin = latestRevenue !== null && latestRevenue > 0 && latestOperating !== null
  ? latestOperating / latestRevenue * 100 : null;
const financialSeries = visibleFinancial.slice().reverse();
const finSelectedLabel:Record<'revenue'|'operatingIncome'|'netIncome'|'freeCashFlow',string> = {
  revenue:'매출',operatingIncome:'영업이익',netIncome:'순이익',freeCashFlow:'잉여현금흐름',
};
const graphValues = financialSeries.map(row=>num(row[finMetric]));
const graphLimit = Math.max(1,...graphValues.map(value=>Math.abs(value ?? 0)));
  const sections:[Tab,string][]=[['SUMMARY','요약'],['CHANGE','변화'],['EVIDENCE','근거'],['FINANCIAL','재무'],['CHART','차트']];

  return <Box sx={{minHeight:'calc(100vh - 64px)',bgcolor:C.bg,color:C.ink,py:{xs:2,md:4}}}>
    <Box sx={{maxWidth:1200,mx:'auto',px:{xs:2,md:4}}}>
      <Button onClick={()=>navigate('/watch')} startIcon={<ArrowBackRoundedIcon/>} sx={{color:C.muted,textTransform:'none',fontWeight:750,mb:1.5}}>내 종목으로</Button>
      <Stack direction={{xs:'column',md:'row'}} justifyContent="space-between" alignItems={{xs:'start',md:'center'}} gap={2}>
        <Box>
          <Typography sx={{color:C.blue,fontSize:12,letterSpacing:'.08em',fontWeight:850}}>STOCK WORKSPACE</Typography>
          <Typography component="h1" sx={{fontSize:{xs:28,md:34},fontWeight:850,letterSpacing:'-.04em',mt:.5}}>{company} <Box component="span" sx={{color:C.muted,fontSize:14,fontWeight:600}}>{ticker}</Box></Typography>
          <Typography sx={{color:C.muted,fontSize:13,mt:.5}}>판단 기준 {basis} · 가격 변동과 기업가치 판단은 분리합니다.</Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          {watchStatus==='saved'?<Chip color="success" variant="outlined" label="관심종목 감시 중"/>:
            <Button disabled={saving||!ticker||watchStatus==='loading'} onClick={()=>void save()} variant="contained" startIcon={saving?<CircularProgress size={16}/>:<AddRoundedIcon/>} sx={{bgcolor:C.blue,textTransform:'none',fontWeight:800}}>
              {watchStatus==='loading'?'등록 여부 확인 중':'이 종목 계속 감시하기'}
            </Button>}
          <Button onClick={()=>navigate('/watch')} variant="outlined" sx={{borderColor:C.line,color:C.navy,textTransform:'none',fontWeight:750}}>내 종목</Button>
        </Stack>
      </Stack>
      {watchStatus==='unknown'&&<Alert severity="info" sx={{mt:2}}>등록 여부를 조회하지 못했습니다. 감시 등록 버튼을 다시 누르면 서버에 현재 상태를 확인합니다.</Alert>}
      {saveFeedback&&<Alert severity={watchStatus==='saved'?'success':'warning'} sx={{mt:2}}>{saveFeedback}</Alert>}

      {overviewLoading?<Box sx={{mt:3}}><Skeleton variant="rounded" height={195}/><Stack direction={{xs:'column',md:'row'}} spacing={2} sx={{mt:2}}><Skeleton variant="rounded" height={140} sx={{flex:1}}/><Skeleton variant="rounded" height={140} sx={{flex:1}}/></Stack></Box>:
        overviewError?<Alert severity="warning" sx={{mt:3}} action={<Button color="inherit" size="small" onClick={()=>setOverviewRetry(v=>v+1)}>다시 조회</Button>}>{overviewError}</Alert>:
        <>
          <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',md:'minmax(0,1.25fr) minmax(0,.75fr)'},gap:2,mt:3}}>
            <Box sx={{bgcolor:C.navy,color:'#fff',borderRadius:3,p:{xs:3,md:4}}}>
              <Typography sx={{fontSize:12,fontWeight:750,color:'#B9CBE0'}}>현재 판단</Typography>
              <Typography sx={{fontSize:27,fontWeight:850,mt:1}}>{judgmentText}</Typography>
              <Typography sx={{fontSize:14,lineHeight:1.85,mt:1.3,color:'#DFE9F4'}}>{reason}</Typography>
            </Box>
            <Box sx={{bgcolor:C.white,border:`1px solid ${C.line}`,borderRadius:3,p:3}}>
              <Typography sx={{fontSize:12,color:C.muted,fontWeight:800}}>관측 가격</Typography>
              <Typography sx={{fontSize:27,fontWeight:850,mt:1}}>{money(price)}</Typography>
              <Typography sx={{fontSize:16,fontWeight:760,color:rate===null?C.unknown:rate>=0?C.green:C.risk}}>{pct(rate)}</Typography>
              <Divider sx={{my:2}}/><Typography sx={{fontSize:12.5,color:C.muted}}>가격과 기업가치 판단의 데이터 기준시각은 다를 수 있습니다.</Typography>
            </Box>
          </Box>
          <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',md:'repeat(3,minmax(0,1fr))'},gap:1.5,mt:2}}>
            {block('왜?',reason)}{block('달라진 것',changeSummary)}{block('다음 확인',next,true)}
          </Box>
        </>}

      <Stack direction="row" gap={.6} role="tablist" aria-label="종목 분석" sx={{mt:3,mb:2,overflowX:'auto',pb:1}}>
        {sections.map(([key,label])=><Button key={key} role="tab" aria-selected={tab===key} onClick={()=>{setTab(key);trackUxEvent('stock_workspace_tab_view',{stock_code:ticker,tab:key});}}
          sx={{borderRadius:2,textTransform:'none',fontWeight:800,minWidth:76,flexShrink:0,bgcolor:tab===key?C.navy:C.white,border:`1px solid ${tab===key?C.navy:C.line}`,color:tab===key?'#fff':C.ink}}>{label}</Button>)}
      </Stack>
      <Box sx={{bgcolor:C.white,border:`1px solid ${C.line}`,borderRadius:3,p:{xs:2,md:3},minHeight:170}} role="tabpanel">
        {tab==='SUMMARY'&&<>
          <Typography component="h2" sx={{fontSize:20,fontWeight:820}}>현재 판단과 다음 행동</Typography>
          {overviewLoading?<CircularProgress size={21} sx={{mt:3}}/>:
            overviewError?<Typography sx={{mt:2,color:C.muted}}>현재 판단을 확인하지 못했습니다. 상단에서 다시 조회해 주세요.</Typography>:
            <><Typography sx={{mt:2,lineHeight:1.85}}>{reason}</Typography><Divider sx={{my:2}}/><Typography sx={{fontWeight:780}}>다음 확인 조건</Typography><Typography sx={{mt:1,lineHeight:1.8}}>{next}</Typography></>}
        </>}
        {tab==='CHANGE'&&<>
          {deepLinkEventId && <Box sx={{bgcolor:'#F0F5FF',border:'1px solid #D5E3FD',borderRadius:2,p:2,mb:2}}>
            <Typography sx={{fontSize:12,fontWeight:850,color:C.blue}}>Inbox에서 선택한 변화</Typography>
            {focusedEvent ? <>
              <Typography sx={{mt:.7,fontWeight:830}}>{first(focusedEvent.title,focusedEvent.eventType,'관측된 변화')}</Typography>
              <Typography sx={{mt:.6,lineHeight:1.8,fontSize:13.5}}>{first(focusedEvent.message,focusedEvent.reason,'별도 설명이 제공되지 않았습니다.')}</Typography>
              {(focusedEvent.previousValue != null || focusedEvent.currentValue != null) &&
                <Typography sx={{mt:.7,fontSize:12.5,color:C.muted}}>
                  {first(focusedEvent.previousValue,'이전값 확인 필요')} → {first(focusedEvent.currentValue,'현재값 확인 필요')}
                </Typography>}
            </> : <Typography sx={{mt:.7,color:C.muted,fontSize:13.5}}>{focusedError||'선택한 이벤트의 원문을 조회 중입니다.'}</Typography>}
          </Box>}
          <Stack direction="row" alignItems="center" justifyContent="space-between"><Typography component="h2" sx={{fontSize:20,fontWeight:820}}>이전 이후 달라진 것</Typography><Button size="small" onClick={fetchChanges} disabled={changesLoading} startIcon={<RefreshRoundedIcon/>}>새로 확인</Button></Stack>
          {changesLoading?<CircularProgress size={22} sx={{mt:2}}/>:changesError?<Alert severity="warning" sx={{mt:2}}>{changesError}</Alert>:
            allChanges.length===0?<Typography sx={{mt:2,color:C.muted}}>표시할 변화가 없습니다. 변화 부재와 데이터 미수집은 구별해야 합니다.</Typography>:
            allChanges.slice(0,15).map((item,i)=><Box key={`${i}:${item}`} sx={{py:1.6,borderBottom:`1px solid ${C.line}`}}><Typography sx={{fontSize:14,lineHeight:1.8}}>{item}</Typography></Box>)}
        </>}
        {tab==='EVIDENCE'&&<>
          <Typography component="h2" sx={{fontSize:20,fontWeight:820}}>판단에 사용된 근거</Typography>
          {reasons.length===0?<Typography sx={{mt:2,color:C.muted}}>확인된 근거가 없습니다. 원문 데이터가 확보되면 판단과 함께 확인할 수 있습니다.</Typography>:
            reasons.map((item,i)=><Box key={`${i}:${item}`} sx={{py:1.6,borderBottom:`1px solid ${C.line}`}}><Typography sx={{fontSize:14,lineHeight:1.8}}>{i+1}. {item}</Typography></Box>)}
          <Button onClick={()=>navigate(`/stocks/${ticker}`)} endIcon={<ArrowForwardRoundedIcon/>} sx={{mt:2,textTransform:'none'}}>원천 및 공시 근거 더 보기</Button>
        </>}

{tab==='FINANCIAL'&&<>
  <Stack direction={{xs:'column',sm:'row'}} alignItems={{xs:'flex-start',sm:'center'}} justifyContent="space-between" spacing={1}>
    <Box>
      <Typography component="h2" sx={{fontSize:20,fontWeight:820}}>재무 체력 · 연간 흐름</Typography>
      <Typography sx={{fontSize:12.5,color:C.muted,mt:.5}}>원천 연간 사업보고서 · 연결(CFS) 우선 · 같은 회계 기준만 비교</Typography>
    </Box>
    <Button size="small" disabled={finLoading} startIcon={<RefreshRoundedIcon/>} onClick={()=>setFinRetry(v=>v+1)}>재무 다시 조회</Button>
  </Stack>
  {finLoading || financialTicker!==ticker ? <Box sx={{mt:2}}><Skeleton variant="rounded" height={190}/><Skeleton sx={{mt:2}} height={130}/></Box> :
    finError && financialTicker===ticker ? <Alert severity="warning" sx={{mt:2}}>{finError}</Alert> : visibleFinancial.length===0 ?
      <Alert severity="info" sx={{mt:2}}>확인된 연간 재무 데이터가 없습니다. 조회 결과를 검증할 수 없습니다.</Alert> : <>
      <Box sx={{bgcolor:'#F5F8FD',border:`1px solid ${C.line}`,borderRadius:2.5,p:2.4,mt:2}}>
        <Typography sx={{fontSize:12,fontWeight:850,color:C.blue,mb:.7}}>현재 재무 해석 · {finLatest?.year}년 {finLatest?.fsDiv}</Typography>
        <Typography sx={{fontSize:14.5,lineHeight:1.8,fontWeight:720}}>{financeTrendMessage(revenueYoY,operatingYoY)}</Typography>
        <Typography sx={{fontSize:12.5,color:latestFcf!==null&&latestFcf<0?C.risk:C.muted,mt:.8,lineHeight:1.7}}>
          {latestFcf===null?'잉여현금흐름은 현재 데이터에서 확인되지 않습니다.':latestFcf<0?'잉여현금흐름이 음수입니다. 투자 지출과 영업현금흐름을 함께 확인하세요.':'잉여현금흐름이 음수가 아닙니다. 현금흐름의 지속성은 별도 확인이 필요합니다.'}
        </Typography>
      </Box>
      <Box sx={{display:'grid',gridTemplateColumns:{xs:'repeat(2,minmax(0,1fr))',md:'repeat(4,minmax(0,1fr))'},gap:1.2,mt:1.5}}>
        {[
          {label:'매출 YoY',value:pct(revenueYoY),note:financialDeltaLabel(revenueYoY)},
          {label:'영업이익 YoY',value:pct(operatingYoY),note:financialDeltaLabel(operatingYoY)},
          {label:'영업이익률',value:pct(margin),note:'최근 연간 매출 기준'},
          {label:'잉여현금흐름',value:won(latestFcf),note:latestFcf===null?'확인 필요':latestFcf<0?'현금흐름 부담':'현금흐름 관측'},
        ].map(metric=><Box key={metric.label} sx={{p:1.8,border:`1px solid ${C.line}`,borderRadius:2.5,minWidth:0}}>
          <Typography sx={{fontSize:12,color:C.muted,fontWeight:760}}>{metric.label}</Typography>
          <Typography sx={{fontSize:{xs:17,md:20},fontWeight:850,mt:.7,overflowWrap:'anywhere'}}>{metric.value}</Typography>
          <Typography sx={{fontSize:11.5,color:C.muted,mt:.35}}>{metric.note}</Typography>
        </Box>)}
      </Box>
      <Box sx={{mt:3,borderTop:`1px solid ${C.line}`,pt:2}}>
        <Typography component="h3" sx={{fontSize:17,fontWeight:820}}>연간 실적 추이</Typography>
        <Typography sx={{fontSize:12,color:C.muted,mt:.3}}>선택한 지표의 연도별 상대 규모를 비교합니다. 0을 기준으로 양수·음수를 구분합니다.</Typography>
        <Stack direction="row" flexWrap="wrap" gap={.7} sx={{mt:1.5,mb:2}}>
          {(['revenue','operatingIncome','netIncome','freeCashFlow'] as const).map(key=><Button size="small" key={key} onClick={()=>setFinMetric(key)} variant={finMetric===key?'contained':'outlined'}
            sx={{textTransform:'none',borderRadius:2,fontWeight:750,bgcolor:finMetric===key?C.navy:undefined,boxShadow:'none'}}>{finSelectedLabel[key]}</Button>)}
        </Stack>
        <Box sx={{display:'grid',gap:1.5}}>
          {financialSeries.map((item,index)=>{
            const value=graphValues[index];
            const width=value===null?0:Math.abs(value)/graphLimit*100;
            return <Box key={`${item.year}:${item.fsDiv}`} sx={{display:'grid',gridTemplateColumns:{xs:'42px minmax(0,1fr)',sm:'54px minmax(0,1fr) 100px'},gap:1,alignItems:'center'}}>
              <Typography sx={{fontSize:12.5,fontWeight:800,color:C.ink}}>{item.year}</Typography>
              <Box sx={{height:22,bgcolor:'#F2F5F9',borderRadius:1,overflow:'hidden',display:'flex',alignItems:'center'}}>
                {value!==null&&<Box sx={{height:12,width:`${width}%`,minWidth:value===0?0:'2px',borderRadius:1,bgcolor:value<0?C.risk:C.blue}}/>}
              </Box>
              <Typography sx={{fontSize:12,textAlign:'right',fontWeight:750,color:value===null?C.unknown:value<0?C.risk:C.ink,gridColumn:{xs:'2',sm:'auto'},mt:{xs:-.5,sm:0}}}>{won(value)}</Typography>
            </Box>;
          })}
        </Box>
        <Typography sx={{fontSize:11.5,color:C.muted,mt:1.5}}>그래프는 선택 지표의 최대 절대값을 100%로 표시합니다. 서로 다른 지표 간 막대 길이는 비교하지 않습니다.</Typography>
      </Box>
      <Box sx={{borderTop:`1px solid ${C.line}`,mt:3,pt:1.5}}>
        <Button onClick={()=>setFinExpanded(value=>!value)} aria-expanded={finExpanded} sx={{textTransform:'none',color:C.navy,fontWeight:820}}>
          {finExpanded?'연도별 재무 원자료 접기':'연도별 재무 원자료 펼치기'}
        </Button>
        <Collapse in={finExpanded}>
          <Box sx={{overflowX:'auto',mt:1}}><Box component="table" sx={{width:'100%',minWidth:580,borderCollapse:'collapse',fontSize:13.5,'& td,& th':{py:1.4,px:1.1,textAlign:'right',borderBottom:`1px solid ${C.line}`},'& td:first-of-type,& th:first-of-type':{textAlign:'left'}}}>
            <thead><tr><th>연도·기준</th><th>매출</th><th>영업이익</th><th>순이익</th><th>영업현금흐름</th><th>FCF</th></tr></thead>
            <tbody>{visibleFinancial.map(row=><tr key={`${row.year}:${row.fsDiv}`}><td>{row.year} · {row.fsDiv}</td><td>{won(row.revenue)}</td><td>{won(row.operatingIncome)}</td><td>{won(row.netIncome)}</td><td>{won(row.operatingCashFlow)}</td><td>{won(row.freeCashFlow)}</td></tr>)}</tbody>
          </Box></Box>
        </Collapse>
      </Box>
      <Button onClick={toLegacy} endIcon={<ArrowForwardRoundedIcon/>} sx={{mt:2,textTransform:'none'}}>재무 전문 데이터 전체 보기</Button>
    </>}
</>}
        {tab==='CHART'&&<>
          <Typography component="h2" sx={{fontSize:20,fontWeight:820,mb:1}}>가격 · 수급 차트</Typography>
          {ticker?<ChartBoundary key={ticker}><Suspense fallback={<Skeleton variant="rounded" height={390}/>}> <MarketSeriesChart ticker={ticker} days={120} view="candles" height={430} showDetailPanel={false}/></Suspense></ChartBoundary>:<Alert severity="warning">유효한 종목코드가 없습니다.</Alert>}
          <Button onClick={toLegacy} endIcon={<ArrowForwardRoundedIcon/>} sx={{mt:2,textTransform:'none'}}>전문 차트 전체 보기</Button>
        </>}
      </Box>
      <Typography sx={{fontSize:12,color:C.unknown,mt:2,lineHeight:1.7}}>확인하지 못한 가격 원인을 확정하지 않습니다. 관심종목 등록은 실제 주식 보유 또는 푸시 알림 수신 동의를 의미하지 않습니다.</Typography>
    </Box>
  </Box>;
}
